import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Logger, LoggingService } from 'ionic-logging-service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, map, retry, retryWhen, switchMap, take, tap } from 'rxjs/operators';
import { LanguageService } from '../language/language.service';
import { IPrinter } from '../printer/models/printer.model';
import { IQueue } from '../printer/models/queue.model';
import { ITenant } from '../tenant/models/tenant.model';
import { IUser } from './models/user.model';
import { StorageService } from '../storage/storage.service';
import { ITenantLite } from '../tenant/models/tenant-lite.model';
import { SanitizationService } from '../sanitization/sanitization.service';

@Injectable({
  providedIn: 'root'
})

export class UserService {

  public userSignal = signal<IUser | null>(null);
  private userRaw: any = null;
  private logger: Logger;

  constructor(
    private httpClient: HttpClient,
    private languageService: LanguageService,
    private loggingService: LoggingService,
    private storageService: StorageService,
    private sanitizationService: SanitizationService
  ) {
    this.logger = this.loggingService.getLogger("[UserService]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  private deserializeLinkObjects(user: IUser, links: any): void {
    if (!links) {
      return;
    }

    // Map of link keys to user object properties
    const linkMappings: Array<{ key: string; target: (url: string | null) => void }> = [
      { key: 'self', target: (url) => user.links.self = url },
      { key: 'px:printjobs', target: (url) => user.links.printJobs = url },
      { key: 'px:idCode', target: (url) => user.links.idCode = url },
      { key: 'px:pincode', target: (url) => user.links.pinCode = url },
      { key: 'px:cards', target: (url) => user.links.cards = url },
      { key: 'px:workflows', target: (url) => user.links.workflows = url },
      { key: 'px:releaseQueues', target: (url) => user.links.releaseQueues = url }
    ];

    // Process each link with validation
    linkMappings.forEach(({ key, target }) => {
      const href = links[key]?.href;
      if (href) {
        target(this.sanitizationService.validateApiUrl(href));
      }
    });
  }

  private deserializeEmbeddedObjects(user: IUser, embedded: any): void {
    if (embedded) {
      // Sanitize text fields to prevent XSS
      user.embedded.authentication_details.source = this.sanitizeTextField(embedded.authentication_details?.source);
      user.embedded.permissions.permissions = embedded.permissions?.permissions || null;
      user.embedded.permissions.role = this.sanitizeTextField(embedded.permissions?.role);
      user.embedded.authentication_details.principal = this.sanitizeTextField(embedded.authentication_details?.principal);
      
      // Validate and sanitize URL
      user.embedded.user.links.self.href = this.sanitizationService.validateApiUrl(embedded.user?._links?.self?.href);
      
      // Sanitize text fields
      user.embedded.user.email = this.sanitizeDisplayField(embedded.user?.email);
      user.embedded.user.name = this.sanitizeDisplayField(embedded.user?.name);
      user.embedded.user.language = this.sanitizeTextField(embedded.user?.language);
    }
  }

  /**
   * Safely sanitizes non-display text fields from API responses to prevent XSS.
   * Strips HTML tags and dangerous characters. Use for fields NOT shown in templates.
   */
  private sanitizeTextField(value: string | undefined | null): string | null {
    if (!value) {
      return null;
    }
    const sanitized = this.sanitizationService.sanitizeTextInput(value);
    return sanitized || null;
  }

  /**
   * Sanitizes user-provided text fields that will be displayed in templates.
   * Preserves all characters so they render as visible plain text.
   */
  private sanitizeDisplayField(value: string | undefined | null): string | null {
    if (!value) {
      return null;
    }
    const sanitized = this.sanitizationService.sanitizeDisplayText(value);
    return sanitized || null;
  }

  private deserializeUserAppSettings(user: IUser, userAppSettings: any): void {
    if (userAppSettings) {
      user.userAppSettings.lastUsedPrinter = userAppSettings.lastUsedPrinter ? userAppSettings.lastUsedPrinter : null;
      user.userAppSettings.lastUsedQueue = userAppSettings.lastUsedQueue ? userAppSettings.lastUsedQueue : null;
      user.userAppSettings.lastUsedNetwork = userAppSettings.lastUsedNetwork ? userAppSettings.lastUsedNetwork : null;
      user.userAppSettings.lastUsedTenant = userAppSettings.lastUsedTenant ? userAppSettings.lastUsedTenant : null;
      user.userAppSettings.changeLogReviewDate = userAppSettings.changeLogReviewDate ? userAppSettings.changeLogReviewDate : null;
      user.userAppSettings.pinnedPrinterQueues = userAppSettings.pinnedPrinterQueues ? userAppSettings.pinnedPrinterQueues : null;
      user.userAppSettings.pinnedPrinters = userAppSettings.pinnedPrinters ? userAppSettings.pinnedPrinters : null;
      user.userAppSettings.securePrintMethods = userAppSettings.securePrintMethods ? userAppSettings.securePrintMethods : null;
      user.userAppSettings.serviceMenuEnabled = userAppSettings.serviceMenuEnabled ? userAppSettings.serviceMenuEnabled : null;
    }
  }

  private deserializeUser(input: any, skipObservers: boolean): IUser {
    let user: IUser = {
      links: {
        self: null,
        printJobs: null,
        idCode: null,
        pinCode: null,
        cards: null,
        releaseQueues: null,
        workflows: null
      },
      embedded: {
        permissions: {
          permissions: null,
          role: null
        },
        authentication_details: {
          source: null,
          principal: null
        },
        user: {
          links: {
            self: {
              href: null
            }
          },
          email: null,
          name: null,
          language: null
        }
      },
      groups: input._embedded.groups ? this.formatGroupsArray(input._embedded.groups) : [],
      userAppSettings: {
        lastUsedPrinter: null,
        lastUsedQueue: null,
        lastUsedNetwork: null,
        lastUsedTenant: null,
        changeLogReviewDate: null,
        pinnedPrinterQueues: null,
        pinnedPrinters: null,
        securePrintMethods: null,
        serviceMenuEnabled: null
      },
      name: input._embedded.user.name ? input._embedded.user.name : null,
      inactivityAutoDelete: input.inactivityAutoDelete ? input.inactivityAutoDelete : false,
      api: {
        getIdCode: (user: IUser) => {
          return this.getIdCode(user);
        },
        setIdCode: (tenant: ITenant) => {
          return this.setIdCode(tenant);
        },
        deleteIdCode: (idCode:any) => {
          return this.deleteIdCode(idCode);
        },
        getPinCode: (user: IUser) => {
          return this.getPinCode(user);
        },
        setPinCode: (user: IUser, pinCode: any) => {
          return this.setPinCode(user, pinCode);
        },
        deletePinCode: (user: IUser) => {
          return this.deletePinCode(user);
        },
        getCards: (user: IUser) => {
          return this.getCards(user);
        },
        deleteCard: (card: any) => {
          return this.deleteCard(card);
        },
        registerCard: (user: IUser, cardCode: any) => {
          return this.registerCard(user, cardCode);
        }
      }
    };
    if (input._links) {this.deserializeLinkObjects(user, input._links);}
    if (input._embedded) {this.deserializeEmbeddedObjects(user, input._embedded);}
    if (input.userAppSettings) {this.deserializeUserAppSettings(user, input.userAppSettings);}
    if (!skipObservers) {
      this.languageService.changeLanguage(user.embedded.user.language);
    }
    return user;
  }

  private formatGroupsArray (groupsArrayRaw): Array<string> {
    let groupsArray: Array<string> = [];
    if (groupsArrayRaw) {
      for (let i = 0; i < groupsArrayRaw.length; i++) {
        groupsArray.push(groupsArrayRaw[i]._links.self.href);
      }
    }

    return groupsArray;
  }

  public refreshCurrentUser(): Observable<void> {
    const methodName = 'refreshCurrentUser() ';
    this.logger.info(methodName);
    return this.storageService.getItemFromLocalStorage('lastUsedTenant', 'getLastUsedTenant()').pipe(
      take(1),
      switchMap((lastUsedTenant: ITenantLite) => {
        // No stored tenant context (e.g. stale/cleared storage, or a user with
        // no tenant) means there is no currentUser link to fetch. Fail with a
        // clear, logged error instead of throwing a cryptic
        // "Cannot read properties of null (reading 'links')" TypeError.
        const currentUserUrl = lastUsedTenant?.links?.currentUser;
        if (!currentUserUrl) {
          this.logger.error(methodName + 'no lastUsedTenant / currentUser link available - cannot refresh user');
          return throwError(() => new Error('Cannot refresh current user: no tenant context available'));
        }
        return this.getUserResource(currentUserUrl).pipe(
          take(1),
          map(() => void 0)
        );
      })
    );
  }

  public getUserResource(currentUserUrl: string): Observable<any> {
    const methodName = 'getUserResource() ';
    this.logger.info(methodName);

    if (!currentUserUrl) {
      this.logger.error(methodName + 'missing currentUser URL');
      return throwError(() => new Error('Cannot load current user: missing currentUser link'));
    }

    return this.httpClient.get<any>(currentUserUrl)
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(1)
        )),
        tap((res: any) => {
          // A 200 with a null / empty / malformed body would otherwise blow up
          // on res._embedded.user below with a cryptic TypeError while the user
          // signal silently stays null - which is the root of the downstream
          // "reading 'userAppSettings'" crashes. Fail explicitly so ErrorService
          // surfaces a real, logged error and the signal is left untouched.
          if (!res || !res._embedded || !res._embedded.user) {
            this.logger.error(methodName + 'empty or invalid user resource body');
            throw new Error('Cannot load current user: empty or invalid response body');
          }
          this.logger.info('currentUserEmail: ' + JSON.stringify(res._embedded.user.email));
          this.logger.info('currentUserID: ' + JSON.stringify(res._embedded.user._links?.self?.href));
          this.userRaw = res;
          this.userSignal.set(this.deserializeUser(res, false));
        }),
        map((res: any) => this.userSignal()),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('getUserResource() error:', error);
          return throwError(() => error);
        })
      );
  }

  public saveLanguageChange(itemToSave: string, user): Observable<void> {
    this.logger.info('saveLanguageChange()');
    let objectToSend: any = {
      op: 'replace',
      path: '/language',
      value: itemToSave
    };

    return this.httpClient.patch(user.embedded.user.links.self.href, [objectToSend])
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(3)
        )),
        map(() => void 0),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('saveLanguageChange() PATCH ERROR - Object to send: ' + JSON.stringify(objectToSend), error);
          return throwError(() => error);
        })
      );
  }

  public resetUser(): Observable<void> {
    this.logger.info('resetUser()');
    return of(this.userSignal.set(null));
  }

  public changePrinterQueuePinStatus(printQueueUrl: string, isPinned: boolean): Observable<void> {
    this.logger.info('changePrinterQueuePinStatus() ' + 'isPinned ' + isPinned);

    if (!isPinned) {
     return this.removePinnedQueue(printQueueUrl);
    } else {
      return this.addPinnedQueue(printQueueUrl);
    }
  }

  private addPinnedQueue(printQueueUrl: string): Observable<void> {
    this.logger.info('addPinnedQueue()');
    let objectToSend: any = {
      op: 'add',
      path: '/userAppSettings/pinnedPrinterQueues/-',
      value: printQueueUrl
    };

    if (this.userRaw && !this.userRaw.userAppSettings) {
      objectToSend.path = '/userAppSettings';
      objectToSend.value = {
        pinnedPrinterQueues: [printQueueUrl]
      };
    } else if (this.userRaw && !this.userRaw.userAppSettings.pinnedPrinterQueues) {
      objectToSend.path = '/userAppSettings/pinnedPrinterQueues';
      objectToSend.value = [printQueueUrl];
    }

    return this.httpClient.patch(this.userSignal().links.self, [objectToSend])
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(3)
        )),
        tap((response) => {
          if (this.userRaw && !this.userRaw.userAppSettings) {
            this.userRaw['userAppSettings'] = {};
            if (this.userRaw && !this.userRaw.userAppSettings.pinnedPrinterQueues) {
              this.userRaw.userAppSettings['pinnedPrinterQueues'] = null;
            }
          }
          this.userRaw.userAppSettings.pinnedPrinterQueues = response['userAppSettings'].pinnedPrinterQueues;
          this.userSignal().userAppSettings.pinnedPrinterQueues = response['userAppSettings'].pinnedPrinterQueues;
        }),
        map(() => void 0),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('addPinnedQueue() PATCH ERROR - Object to send: ' + JSON.stringify(objectToSend), error);
          return throwError(() => error);
        })
      );
  }

  private removePinnedQueue(printQueueUrl: string): Observable<void> {
    this.logger.info('removePinnedQueue(): ' + printQueueUrl);
    let objectToSend: any = {};

    let indexOfPrinter = this.userRaw.userAppSettings && this.userRaw.userAppSettings.pinnedPrinterQueues ? this.userRaw.userAppSettings.pinnedPrinterQueues.indexOf(printQueueUrl) : -1;
    if (indexOfPrinter > -1) {
      objectToSend.op = 'remove';
      objectToSend.path = '/userAppSettings/pinnedPrinterQueues/' + indexOfPrinter;
      delete objectToSend.value;

      return this.httpClient.patch(this.userSignal().links.self, [objectToSend])
        .pipe(
          retryWhen(error => error.pipe(
            delay(1000),
            take(3)
          )),
          tap((response) => {
            if (this.userRaw && !this.userRaw.userAppSettings) {
              this.userRaw['userAppSettings'] = {};
              if (this.userRaw && !this.userRaw.userAppSettings.pinnedPrinterQueues) {
                this.userRaw.userAppSettings['pinnedPrinterQueues'] = null;
              }
            }
            this.userRaw.userAppSettings.pinnedPrinterQueues = response['userAppSettings'].pinnedPrinterQueues;
            this.userSignal().userAppSettings.pinnedPrinterQueues = response['userAppSettings'].pinnedPrinterQueues;
          }),
          map(() => void 0),
          catchError((error: HttpErrorResponse) => {
            this.logger.error('removePinnedQueue() PATCH ERROR - Object to send: ' + JSON.stringify(objectToSend), error);
            return throwError(() => error);
          })
        );
    } else {
      this.logger.info('Queue is not saved in userAppSettings');
      return;
    }
  }

  public setAvailablePinnedPrinterQueues(queueUrls: Array<string>): Observable<void> {
    this.logger.info('setAvailablePinnedPrinterQueues()');

    let objectToPatch: Array<any> = [];

    objectToPatch.push(
      {
        'op': 'remove',
        'path': '/userAppSettings/pinnedPrinterQueues'
      }
    );

    if (queueUrls.length > 0) {
      objectToPatch.push(
        {
          'op': 'add',
          'path': '/userAppSettings/pinnedPrinterQueues/',
          'value': []
        }
      );
      queueUrls.forEach((url) => {
        objectToPatch.push(
          {
            'op': 'add',
            'path': '/userAppSettings/pinnedPrinterQueues/-',
            'value': url
          }
        );
      });
    }

    return this.httpClient.patch(this.userSignal().links.self, objectToPatch)
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(3)
        )),
        tap((response) => {
          if (this.userRaw && !this.userRaw.userAppSettings) {
            this.userRaw['userAppSettings'] = {};
            if (this.userRaw && !this.userRaw.userAppSettings.pinnedPrinterQueues) {
              this.userRaw.userAppSettings['pinnedPrinterQueues'] = null;
            }
          }
          this.userRaw.userAppSettings.pinnedPrinterQueues = response['userAppSettings'].pinnedPrinterQueues;
          this.userSignal().userAppSettings.pinnedPrinterQueues = response['userAppSettings'].pinnedPrinterQueues;
        }),
        map(() => void 0),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('setAvailablePinnedPrinterQueues() PATCH ERROR - Object to send: ' + JSON.stringify(objectToPatch), error);
          return throwError(() => error);
        })
      );
  }

  public changePrinterPinStatus(printerUrls: Array<string>, isPinned: boolean): Observable<void> {
    this.logger.info('changePrinterPinStatus() to NO pinned printers');

    let objectToPatch: Array<any> = [];

    printerUrls.forEach(url => {
      objectToPatch.push(
        {
          "op": "remove",
          path: '/userAppSettings/pinnedPrinters/' + 0
        }
      );
    });

    return this.httpClient.patch(this.userSignal().links.self, [objectToPatch])
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(3)
        )),
        tap((response) => {
          if (this.userRaw && !this.userRaw.userAppSettings) {
            this.userRaw['userAppSettings'] = {};
            if (this.userRaw && !this.userRaw.userAppSettings.pinnedPrinters) {
              this.userRaw.userAppSettings['pinnedPrinters'] = null;
            }
          }
          this.userRaw.userAppSettings.pinnedPrinters = response['userAppSettings'].pinnedPrinters;
          this.userSignal().userAppSettings.pinnedPrinters = response['userAppSettings'].pinnedPrinters;
        }),
        map(() => void 0),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('changePrinterPinStatus() PATCH ERROR - Object to send: ' + JSON.stringify(objectToPatch), error);
          return throwError(() => error);
        })
      );
  }

  public setLastUsedPrinter = function (printer: IPrinter): Observable<void> {
    this.logger.info('setLastUsedPrinter()');
    let objectToSend: any = {
      op: 'replace',
      path: '/userAppSettings/lastUsedPrinter',
      value: printer.links.self,
    };
    if (this.userRaw && !this.userRaw.userAppSettings) {
      objectToSend = {
        op: 'add',
        path: '/userAppSettings',
        value: {
          lastUsedPrinter: printer.links.self
        }
      };
    } else {
      if (this.userRaw && !this.userRaw.userAppSettings.lastUsedPrinter) {
        objectToSend.op = 'add';
      }
    }

    return this.httpClient.patch(this.userSignal().links.self, [objectToSend])
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(3)
        )),
        tap((response) => {
          if (this.userRaw && !this.userRaw.userAppSettings) {
            this.userRaw['userAppSettings'] = {};
            if (this.userRaw && !this.userRaw.userAppSettings.lastUsedPrinter) {
              this.userRaw.userAppSettings['lastUsedPrinter'] = null;
            }
          }
          this.userRaw.userAppSettings.lastUsedPrinter = response['userAppSettings'].lastUsedPrinter;
          this.userSignal().userAppSettings.lastUsedPrinter = response['userAppSettings'].lastUsedPrinter;
        }),
        map(() => void 0),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('setLastUsedPrinter() PATCH ERROR - Object to send: ' + JSON.stringify(objectToSend), error);
          return throwError(() => error);
        })
      );
  };

  public setLastUsedQueue = function (queue: IQueue): Observable<void> {
    this.logger.info('setLastUsedQueue()');
    let objectToSend: any = {
      op: 'replace',
      path: '/userAppSettings/lastUsedQueue',
      value: queue.links.self,
    };
    if (this.userRaw && !this.userRaw.userAppSettings) {
      objectToSend = {
        op: 'add',
        path: '/userAppSettings',
        value: {
          lastUsedQueue: queue.links.self
        }
      };
    } else {
      if (this.userRaw && !this.userRaw.userAppSettings.lastUsedQueue) {
        objectToSend.op = 'add';
      }
    }
    return this.httpClient.patch(this.userSignal().links.self, [objectToSend])
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(3)
        )),
        tap((response) => {
          if (this.userRaw && !this.userRaw.userAppSettings) {
            this.userRaw['userAppSettings'] = {};
            if (this.userRaw && !this.userRaw.userAppSettings.lastUsedQueue) {
              this.userRaw.userAppSettings['lastUsedQueue'] = null;
            }
          }

          this.userRaw.userAppSettings.lastUsedQueue = response['userAppSettings'].lastUsedQueue;
          this.userSignal().userAppSettings.lastUsedQueue = response['userAppSettings'].lastUsedQueue;
        }),
        map(() => void 0),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('setLastUsedQueue() PATCH ERROR - Object to send: ' + JSON.stringify(objectToSend), error);
          return throwError(() => error);
        })
      );
  };

  public setSecurePrintMethods = function (securePrintMethods: Array<string>): Observable<void> {
    this.logger.info('setSecurePrintMethods()');
    let objectToPatch: any = {};
    if (this.userRaw && !this.userRaw.userAppSettings) {
      objectToPatch = [
        {
          op: 'add',
          path: '/userAppSettings',
          value: {
            securePrintMethods: securePrintMethods
          }
        }
      ];
    } else {
      if (this.userRaw && !this.userRaw.userAppSettings.securePrintMethods) {
        objectToPatch = [
          {
            op: 'add',
            path: '/userAppSettings/securePrintMethods',
            value: securePrintMethods
          }
        ];
      } else {
        objectToPatch = [
          {
            op: 'replace',
            path: '/userAppSettings/securePrintMethods',
            value: securePrintMethods
          }
        ];
      }
    }

    return this.httpClient.patch(this.userSignal() && this.userSignal().links.self, objectToPatch)
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(3)
        )),
        tap((response) => {
          if (this.userRaw && !this.userRaw.userAppSettings) {
            this.userRaw['userAppSettings'] = {};
            if (this.userRaw && !this.userRaw.userAppSettings.securePrintMethods) {
              this.userRaw.userAppSettings['securePrintMethods'] = null;
            }
          }

          this.userRaw.userAppSettings.securePrintMethods = response['userAppSettings'].securePrintMethods;
          this.userSignal().userAppSettings.securePrintMethods = response['userAppSettings'].securePrintMethods;
        }),
        map(() => void 0),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('setSecurePrintMethods() PATCH ERROR - Object to send: ' + JSON.stringify(objectToPatch), error);
          return throwError(() => error);
        })
      );
  };

  public removeLastUsedPrinterAndQueue(): Observable<void> {
    this.logger.info('removeLastPrinterAndQueue()');
    let objectToSend: Array<any> = [];

    objectToSend.push(
      {
        op: 'remove',
        path: '/userAppSettings/lastUsedPrinter'
      },
      {
        op: 'remove',
        path: '/userAppSettings/lastUsedQueue'
      }
    );

    return this.httpClient.patch(this.userSignal().links.self, [objectToSend])
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(3)
        )),
        tap((response) => {
          if (this.userRaw && !this.userRaw.userAppSettings) {
            this.userRaw['userAppSettings'] = {};
            if (this.userRaw && !this.userRaw.userAppSettings.lastUsedPrinter) {
              this.userRaw.userAppSettings['lastUsedPrinter'] = null;
            }
          }
          this.userRaw.userAppSettings.lastUsedPrinter = response['userAppSettings'].lastUsedPrinter;
          this.userSignal().userAppSettings.lastUsedPrinter = response['userAppSettings'].lastUsedPrinter;
        }),
        map(() => void 0),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('removeLastPrinterAndQueue() PATCH ERROR - Object to send: ' + JSON.stringify(objectToSend), error);
          return throwError(() => error);
        })
      );
  }

  public removeAllPinnedQueues(): Observable<void> {
    this.logger.info('removeAllPinnedQueues()');
    let objectToSend: Array<any> = [];

    objectToSend.push(
      {
        op: 'remove',
        path: '/userAppSettings/pinnedPrinterQueues'
      }
    );


    return this.httpClient.patch(this.userSignal().links.self, [objectToSend])
      .pipe(
        retryWhen(error => error.pipe(
          delay(1000),
          take(3)
        )),
        tap((response) => {
          if (this.userRaw && !this.userRaw.userAppSettings) {
            this.userRaw['userAppSettings'] = {};
            if (this.userRaw && !this.userRaw.userAppSettings.pinnedPrinterQueues) {
              this.userRaw.userAppSettings['pinnedPrinterQueues'] = null;
            }
          }
          this.userRaw.userAppSettings.pinnedPrinterQueues = response['userAppSettings'].pinnedPrinterQueues;
          this.userSignal().userAppSettings.pinnedPrinterQueues = response['userAppSettings'].pinnedPrinterQueues;
        }),
        map(() => void 0),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('removeAllPinnedQueues() PATCH ERROR - Object to send: ' + JSON.stringify(objectToSend), error);
          return throwError(() => error);
        })
      );
  }

  public getIdCode(user: IUser): Observable<any> {
    this.logger.info('getIdCode()');
    const idCodeUrl = user.links.idCode;

    return this.httpClient.get<any>(idCodeUrl)
    .pipe(
      retry(1),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  public setIdCode(tenant: ITenant): Observable<any> {
    this.logger.info('setIdCode()');
    let idCodesUrl = tenant.links.idCodes;

    return this.httpClient.post<any>(idCodesUrl, {}).pipe(
      retry(1),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('setIdCode() error:' + JSON.stringify(error));
        return throwError(() => error);
      })
    );
  }

  public deleteIdCode(idCodeResource:any): Observable<any> {
    this.logger.info('deleteIdCode()');

    return this.httpClient.delete<any>(idCodeResource._links.self.href).pipe(
      retry(1),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('deleteIdCode() error:' + JSON.stringify(error));
        return throwError(() => error);
      })
    );
  }

  public getPinCode(user: IUser): Observable<any> {
    this.logger.info('getPinCode()');
    const pinCodeUrl = user.links.pinCode;
    return this.httpClient.get<any>(pinCodeUrl).pipe(
      retry(1),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  public setPinCode(user: IUser, pinCode): Observable<any> {
    this.logger.info('setPinCode()');
    let pinCodeUrl = user.links.pinCode;
    let postObject = {
      pincode: pinCode
    };
    return this.httpClient.post<any>(pinCodeUrl, postObject).pipe(
      retry(1),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  public deletePinCode(pinCodeResource:any): Observable<any> {
    this.logger.info('deletePinCode()');
    return this.httpClient.delete<any>(pinCodeResource._links.self.href).pipe(
      retry(1),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  public getCards(user: IUser): Observable<any> {
    this.logger.info('getCards()');
    const cardsUrl = user.links.cards;

    return this.httpClient.get<any>(cardsUrl).pipe(
      retry(1),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  public deleteCard (card: any): Observable<any> {
    this.logger.info('deleteCard()');
    let cardUrl = card._links.self.href;
    return this.httpClient.delete<any>(cardUrl).pipe(
      retry(1),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  public registerCard(user: IUser, cardCode: any): Observable<any> {
    this.logger.info('registerCard()');
    let cardUrl = user.links.cards;
    let postObject = {
      registrationCode: cardCode
    };
    return this.httpClient.post<any>(cardUrl, postObject).pipe(
      retry(1),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }
}